import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { MapPin } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { motion, AnimatePresence } from 'framer-motion';

// URL to the TopoJSON file we downloaded
const geoUrl = "/indonesia-topo.json";

const regions = [
    {
        id: 'aceh',
        name: 'Sumatra Aceh Gayo',
        coordinates: [96.909, 4.298],
        description: 'Known for its bold yet refined character. Full body with low to medium acidity.',
        notes: ['Dark Chocolate', 'Brown Sugar', 'Roasted Nuts', 'Herbal'],
        body: 'Medium-Full',
        acidity: 'Low-Medium',
        brew: 'V60, Kalita, Pour Over'
    },
    {
        id: 'java',
        name: 'Java Ijen',
        coordinates: [114.227, -8.058],
        description: 'Reflects Java’s classic profile with a clean and structured character. Balanced acidity.',
        notes: ['Caramel', 'Cocoa', 'Toasted Nuts', 'Spice'],
        body: 'Medium-Full',
        acidity: 'Medium',
        brew: 'Espresso, V60, French Press'
    },
    {
        id: 'bali',
        name: 'Bali Kintamani',
        coordinates: [115.362, -8.343],
        description: 'Fresh and vibrant character. Medium body with bright acidity.',
        notes: ['Citrus', 'Tropical Fruit', 'Floral', 'Caramel'],
        body: 'Medium-Full',
        acidity: 'Medium',
        brew: 'Espresso, V60, Pour Over'
    },
    {
        id: 'flores',
        name: 'Flores Bajawa',
        coordinates: [120.972, -8.761],
        description: 'Shaped by rich soil and cool mountain climate. Bright yet balanced acidity.',
        notes: ['Caramel', 'Cocoa', 'Citrus', 'Spice'],
        body: 'Medium-Full',
        acidity: 'Medium',
        brew: 'Espresso, V60, Pour Over'
    },
    {
        id: 'sulawesi',
        name: 'Sulawesi Toraja',
        coordinates: [119.894, -3.100],
        description: 'Rich and comforting cup. Full body with low acidity.',
        notes: ['Dark Chocolate', 'Spices', 'Earthy', 'Herbal'],
        body: 'Full',
        acidity: 'Low',
        brew: 'Espresso, V60, French Press'
    },
    {
        id: 'papua',
        name: 'Papua Wamena',
        coordinates: [138.943, -4.102],
        description: 'Celebrated for its clean and balanced profile. Bright yet gentle acidity.',
        notes: ['Citrus', 'Floral', 'Caramel'],
        body: 'Medium',
        acidity: 'Medium',
        brew: 'V60, Kalita, Pour Over'
    }
];

const IndonesianMap = () => {
    const [selectedRegion, setSelectedRegion] = useState(regions[0]);

    return (
        <div className="w-full bg-[#f8f9fa] rounded-3xl p-6 md:p-12">
            <div className="grid lg:grid-cols-12 gap-8 items-start">

                {/* Map Section */}
                <div className="lg:col-span-8 relative min-h-[400px] md:min-h-[500px] bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" style={{ touchAction: 'pan-y' }}>
                    <ComposableMap
                        projection="geoMercator"
                        projectionConfig={{
                            center: [118, -2],
                            scale: 1000
                        }}
                        style={{ width: "100%", height: "100%" }}
                    >
                        <ZoomableGroup center={[118, -2]} zoom={1} disablePanning disableZooming>
                            <Geographies geography={geoUrl}>
                                {({ geographies }) =>
                                    geographies.map((geo) => (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            fill="#EAEAEC"
                                            stroke="#D6D6DA"
                                            strokeWidth={0.5}
                                            style={{
                                                default: { outline: "none", pointerEvents: "none" },
                                                hover: { fill: "#D6D6DA", outline: "none", pointerEvents: "none" },
                                                pressed: { fill: "#E42", outline: "none", pointerEvents: "none" },
                                            }}
                                        />
                                    ))
                                }
                            </Geographies>

                            {regions.map((region) => (
                                <Marker
                                    key={region.id}
                                    coordinates={region.coordinates}
                                    onClick={() => setSelectedRegion(region)}
                                    className="cursor-pointer group"
                                >
                                    {/* Ripple Effect Ring */}
                                    <circle
                                        r={10}
                                        fill={selectedRegion.id === region.id ? "#C9A14F" : "transparent"}
                                        fillOpacity={0.3}
                                        className={selectedRegion.id === region.id ? "animate-ping" : ""}
                                    />

                                    {/* Main Dot */}
                                    <circle
                                        r={5}
                                        fill={selectedRegion.id === region.id ? "#C9A14F" : "#31415A"}
                                        stroke="#fff"
                                        strokeWidth={2}
                                        className="transition-colors duration-300 drop-shadow-md"
                                    />

                                    {/* Text Label */}
                                    <text
                                        textAnchor="middle"
                                        y={-15}
                                        style={{
                                            fontFamily: "system-ui",
                                            fill: "#31415A",
                                            fontSize: "8px",
                                            fontWeight: "bold",
                                            textShadow: "0px 1px 2px rgba(255,255,255,1)"
                                        }}
                                        className={`transition-opacity duration-300 ${selectedRegion.id === region.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                                    >
                                        {region.name}
                                    </text>
                                </Marker>
                            ))}
                        </ZoomableGroup>
                    </ComposableMap>

                </div>

                {/* Info Panel Section */}
                <div className="lg:col-span-4">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={selectedRegion ? selectedRegion.id : 'empty'}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Card className="border-none shadow-xl bg-white overflow-hidden">
                                <div className="h-2 w-full bg-[#C9A14F]" />
                                <CardContent className="p-4 md:p-8">
                                    <div className="flex items-center gap-2 mb-3 text-[#C9A14F]">
                                        <MapPin className="w-4 h-4 md:w-5 md:h-5" />
                                        <span className="text-xs md:text-sm font-bold tracking-wider uppercase">Selected Region</span>
                                    </div>

                                    <h3 className="text-xl md:text-3xl font-bold text-[#31415A] mb-3 md:mb-4">
                                        {selectedRegion.name}
                                    </h3>

                                    <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-8 leading-relaxed">
                                        {selectedRegion.description}
                                    </p>

                                    <div className="space-y-4 md:space-y-6">
                                        <div>
                                            <h4 className="text-xs md:text-sm font-bold text-[#31415A] uppercase tracking-wide mb-2 md:mb-3">Flavor Notes</h4>
                                            <div className="flex flex-wrap gap-1.5 md:gap-2">
                                                {selectedRegion.notes.map((note, idx) => (
                                                    <span key={idx} className="px-2 py-0.5 md:px-3 md:py-1 bg-[#F5F5F0] text-[#5C5935] text-xs md:text-sm font-medium rounded-full border border-[#E6E6E0]">
                                                        {note}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 md:gap-4 pt-3 md:pt-4 border-t border-gray-100">
                                            <div>
                                                <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Body</span>
                                                <span className="text-sm md:text-base font-semibold text-[#31415A]">{selectedRegion.body}</span>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Acidity</span>
                                                <span className="text-sm md:text-base font-semibold text-[#31415A]">{selectedRegion.acidity}</span>
                                            </div>
                                        </div>

                                        <div className="pt-2">
                                            <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Recommended Brew</span>
                                            <span className="text-sm md:text-base font-semibold text-[#31415A]">{selectedRegion.brew}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default IndonesianMap;
